import React, { FC, useState } from 'react';
import {
  Fade,
  Grid,
  Box,
  TextField,
  MenuItem,
  FormControl,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Modal, Loader, Card, ModalSaveSection, SMBSelect } from '../../../components';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { IDropdownResponse, IAddMaterial } from '../../../models';
import { Assignment } from '@mui/icons-material';
import { getUnitsOfMeasure, getBidFrequencies, createMaterial } from '../../../fetch';
import { ManageMaterialsFiltersDropdown } from '../manage-materials/manage-materials-filters-dropdown';

interface ICreateMaterialModal {
  open: boolean;
  onClose: () => void;
}

export const CreateMaterialModal: FC<ICreateMaterialModal> = ({ open, onClose }) => {
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState<string>('');
  const [selectedMaterialClassIdFilter, setSelectedMaterialClassIdFilter] = useState(null);
  const [isMaterialActive, setIsMaterialActive] = useState<string>('');
  const [unitOfMeasureId, setUnitOfMeasureId] = useState<number | string>('');
  const [bidFrequencyId, setBidFrequencyId] = useState<number | string>('');
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    setUnitOfMeasureId('');
    setBidFrequencyId('');
    setName('');
    setIsMaterialActive('');
    onClose();
  };

  const { isLoading: isLoadingBidFrequencies, data: bidFrequencies } = useQuery<
    IDropdownResponse[],
    Error
  >(['getBidFrequencies'], () => getBidFrequencies());

  const { isLoading: isLoadingUnitsOfMeasure, data: unitsOfMeasure } = useQuery<
    IDropdownResponse[],
    Error
  >(['getUnitsOfMeasure'], () => getUnitsOfMeasure());

  return (
    <>
      <Modal
        title="Add New Material"
        open={open}
        onClose={() => {
          handleClose();
        }}
        maxWidth="md"
      >
        {isSaving && <Loader type="overlay" position="centered" />}
        <Fade in={open}>
          <Box mt={1}>
            <Card title="Info" icon={<Assignment />}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    autoComplete="nope"
                    label="Name"
                    name="name"
                    size="small"
                    value={name}
                    onChange={({ target: { value } }) => setName(value)}
                  />
                </Grid>
                <ManageMaterialsFiltersDropdown
                  required={true}
                  materialClassId={null}
                  depth={0}
                  setSelectedMaterialClassIdFilter={setSelectedMaterialClassIdFilter}
                  width={6}
                />
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    required
                    onChange={({ target: { value } }) => setUnitOfMeasureId(value)}
                    variant="outlined"
                    handleClear={() => setUnitOfMeasureId('')}
                    name={`unitOfMeasureId`}
                    label={`Unit of Measure`}
                    id={`unitOfMeasureId`}
                    value={unitOfMeasureId}
                    disabled={isLoadingUnitsOfMeasure}
                  >
                    {unitsOfMeasure &&
                      unitsOfMeasure.map((unit: any, index) => {
                        return (
                          <MenuItem key={`${index + 1}`} value={unit.value}>
                            {unit.description}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    required
                    onChange={({ target: { value } }) => setBidFrequencyId(value)}
                    variant="outlined"
                    handleClear={() => setBidFrequencyId('')}
                    name={`bidFrequencyId`}
                    label={`Bid Frequency`}
                    id={`bidFrequency`}
                    value={bidFrequencyId}
                    disabled={isLoadingBidFrequencies}
                  >
                    {bidFrequencies &&
                      bidFrequencies.map((bid: any, index) => {
                        return (
                          <MenuItem key={`${index + 1}`} value={bid.value}>
                            {bid.description}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box padding="0.5rem">
                    <FormControl fullWidth variant="outlined" size="small" required>
                      <Typography>Is Material Active*</Typography>
                      <RadioGroup
                        sx={{
                          position: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'left',
                          alignItems: 'start'
                        }}
                        value={isMaterialActive}
                        onChange={({ target: { value } }) => setIsMaterialActive(value)}
                        aria-label="isMaterialActive"
                        name="isMaterialActive"
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </Card>
            <ModalSaveSection
              isSaveDisabled={
                !name ||
                !unitOfMeasureId ||
                !isMaterialActive ||
                !selectedMaterialClassIdFilter ||
                !bidFrequencyId
              }
              handleCancel={() => {
                handleClose();
              }}
              handleSave={async () => {
                try {
                  setIsSaving(true);
                  const data: IAddMaterial = {
                    name: name,
                    materialClassId: selectedMaterialClassIdFilter,
                    unitOfMeasureId: unitOfMeasureId,
                    bidFrequencyId: bidFrequencyId,
                    isActive: isMaterialActive === 'Yes' ? true : false
                  };

                  const material = await createMaterial(data);
                  enqueueSnackbar('Success, material created!', {
                    variant: 'success'
                  });

                  handleClose();
                  history.push(
                    `/development/parts/manage-materials/${material.data}/general-information`
                  );
                } catch (error) {
                  enqueueSnackbar(error?.Detail ?? `Error creating material, please try again.`, {
                    variant: 'error'
                  });
                  console.log(error);
                } finally {
                  setIsSaving(false);
                }
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
